import React, { useEffect, useState } from 'react';

import { Grid } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';

import { useAppDispatch } from '@/store/hooks';
import { appointmentApiSlice } from '@/store/slices/api/appointmentApiSlice';
import {
  patientsApiSlice,
  useUpdateDeviceLeadsMutation
} from '@/store/slices/api/patientsApiSlice';
import {
  tasksApiSlice,
  useCancelTaskMutation,
  useLazyListTasksQuery
} from '@/store/slices/api/tasksApiSlice';
import { useUpdateDeviceMutation } from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';
import { API_SLICE } from '@/utils/constants/apiEndpoints';
import { isInactiveDeviceStatus } from '@/utils/deviceStatus';
import { prepareLeadsForSave } from '@/utils/leadChambers';

import {
  DEVICE_INACTIVE_TASK_CANCEL_REASON,
  loadOpenPatientTasks,
  tasksToCancelOnDeviceDeactivate
} from '@/pages/physician/deactivateOpenTasks';
import { LabelAndValue } from '@/types/custom';
import { DeviceVendor, Lead } from '@/types/matchDevice';
import DatePickerWrapper from '@/ui/DatePickerWrapper';
import Dropdown from '@/ui/Dropdown';
import CustomModal from '@/ui/Modal';
import TextInputWrapper from '@/ui/TextInputWrapper';

import LeadsEditModal from '../../DeviceLeads/LeadsEditModal/newLeads';

import styles from './DeviceEditModal.module.scss';

export const DEVICE_TYPE_OPTIONS = ['CRT-D', 'CRT-P', 'ILR', 'PM', 'ICD'].map((value) => ({
  label: value,
  value
}));

const ABBOTT_EXTRA_TYPE_OPTIONS = [{ label: 'Leadless PM', value: 'Leadless PM' }];

const getDeviceTypeOptions = (vendor: DeviceVendor | undefined) =>
  vendor === 'abbott'
    ? [...DEVICE_TYPE_OPTIONS, ...ABBOTT_EXTRA_TYPE_OPTIONS]
    : DEVICE_TYPE_OPTIONS;

export const MANUFACTURER_OPTIONS: LabelAndValue[] = [
  { label: 'Abbott', value: 'abbott' },
  { label: 'Boston Scientific', value: 'boston_scientific' },
  { label: 'Medtronic', value: 'medtronic' },
  { label: 'Biotronik', value: 'biotronik' },
  { label: 'Sorin', value: 'sorin' }
];

export const STATUS_OPTIONS: LabelAndValue[] = [
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' }
];

const MRI_COMPATIBILITY_OPTIONS: LabelAndValue[] = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' }
];

const DEVICE_TELEMETRY_OPTIONS: LabelAndValue[] = [
  { label: 'Wireless', value: 'Wireless' },
  { label: 'Manual', value: 'Manual' }
];

interface DeviceEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  deviceInfo?: DeviceInfo;
  reportId?: number;
  leadsData?: Lead[] | undefined;
  isPatientView?: boolean;
}

type DeviceInfo = {
  modelName?: string;
  modelNumber?: string;
  modelNumber2?: string;
  serialNumber?: string;
  serialNumber2?: string;
  type?: string;
  implantDate?: string | null;
  vendor?: DeviceVendor;
  status?: string;
  patientId?: string;
  id?: string;
  deviceTelemetry?: string;
  isMriCompatibility?: boolean | string | null;
};

const getErrorMessage = (error: any): string => {
  const msg = error?.data?.message;
  if (msg) return Array.isArray(msg) ? msg.join(' ') : String(msg).trim();
  if (error?.message) return String(error.message).trim();
  return 'Failed to update device information.';
};

const DeviceEditModal: React.FC<DeviceEditModalProps> = ({
  isOpen,
  onClose,
  deviceInfo,
  reportId,
  leadsData: initialLeadsData,
  isPatientView = false
}) => {
  const [formDeviceInfo, setFormDeviceInfo] = useState<Partial<DeviceInfo>>({});
  const [isEditLead, setIsEditLead] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [leadsData, setLeadsData] = useState<Lead[] | undefined>(initialLeadsData);
  const [leadStatusErrors, setLeadStatusErrors] = useState<Record<string, string>>({});
  const dispatch = useAppDispatch();
  const [updateDeviceInfo] = useUpdateDeviceMutation();
  const [updateDeviceLeads] = useUpdateDeviceLeadsMutation();
  const [cancelTask] = useCancelTaskMutation();
  const [listTasks] = useLazyListTasksQuery();

  const isILRDevice = formDeviceInfo.type === 'ILR' || formDeviceInfo.type === 'Leadless PM';
  const isLeadlessPM = formDeviceInfo.type === 'Leadless PM';

  useEffect(() => {
    if (isOpen && deviceInfo && !isSaving) {
      const allowedFields: (keyof DeviceInfo)[] = [
        'modelName',
        'modelNumber',
        'serialNumber',
        'type',
        'implantDate',
        'vendor',
        'status',
        'patientId',
        'id',
        'deviceTelemetry',
        'isMriCompatibility'
      ];
      const filteredDeviceInfo: Partial<DeviceInfo> = {};
      allowedFields.forEach((field) => {
        if (field in deviceInfo) {
          if (field === 'vendor') {
            filteredDeviceInfo.vendor = deviceInfo.vendor ?? undefined;
          } else if (field === 'isMriCompatibility') {
            // handled below
          } else {
            filteredDeviceInfo[field] = deviceInfo[field] as any;
          }
        }
      });
      filteredDeviceInfo.isMriCompatibility =
        deviceInfo.isMriCompatibility == null ? '' : deviceInfo.isMriCompatibility;

      // Detect Leadless PM by colon-joined values (backend stores as PM type)
      const hasColonModel = (deviceInfo.modelNumber ?? '').includes(':');
      const hasColonSerial = (deviceInfo.serialNumber ?? '').includes(':');
      if (deviceInfo.type === 'Leadless PM' || hasColonModel || hasColonSerial) {
        const [modelNumber, modelNumber2] = (deviceInfo.modelNumber ?? '').split(':');
        const [serialNumber, serialNumber2] = (deviceInfo.serialNumber ?? '').split(':');
        filteredDeviceInfo.type = 'Leadless PM';
        filteredDeviceInfo.modelNumber = modelNumber ?? '';
        filteredDeviceInfo.modelNumber2 = modelNumber2 ?? '';
        filteredDeviceInfo.serialNumber = serialNumber ?? '';
        filteredDeviceInfo.serialNumber2 = serialNumber2 ?? '';
      }

      setFormDeviceInfo(filteredDeviceInfo);
    }
  }, [isOpen, deviceInfo]);

  useEffect(() => {
    if (isOpen) {
      setLeadsData(initialLeadsData);
    }
  }, [isOpen, initialLeadsData]);

  // Handler to update a field in a lead
  const handleLeadFieldChange = (leadId: string, field: keyof Lead, value: any) => {
    setLeadsData((prev) =>
      prev?.map((lead) => (lead.id === leadId ? { ...lead, [field]: value } : lead))
    );
  };

  // Handler to add a new lead to a chamber
  const handleAddLead = (newLead: Lead) => {
    // Convert NewLead to Lead type
    const convertedLead: Lead = {
      ...newLead,
      leadManufacturer:
        newLead.leadManufacturer && typeof newLead.leadManufacturer === 'object'
          ? {
              leadVendorId:
                typeof newLead.leadManufacturer.leadVendorId === 'string'
                  ? Number(newLead.leadManufacturer.leadVendorId) || null
                  : (newLead.leadManufacturer.leadVendorId ?? null)
            }
          : undefined
    };
    setLeadsData((prev) => [...(prev || []), convertedLead]);
  };

  // Handler to delete a lead
  const handleDeleteLead = (leadId: string) => {
    setLeadsData((prev) => prev?.filter((lead) => lead.id !== leadId));
  };

  // Handler for lead status error
  const handleLeadStatusError = (leadId: string, error?: string) => {
    setLeadStatusErrors((prev) => {
      if (error) return { ...prev, [leadId]: error };
      const { [leadId]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleFieldChange = (field: keyof DeviceInfo, value: string | DeviceVendor) => {
    setFormDeviceInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleImplantDateChange = (date: Date | null) => {
    if (date === undefined) return;
    const dateString = date === null ? '' : dayjs(date).format('MM/DD/YYYY');
    handleFieldChange('implantDate', dateString);
  };

  const handleSave = async () => {
    console.log({ leadsData });

    // For ILR devices or non-patient view, skip the leads step
    if (isPatientView && !isEditLead && !isILRDevice) {
      setIsEditLead(true);
      return;
    }

    let deviceLeadPayload;
    if (!isILRDevice) {
      const prepared = prepareLeadsForSave(leadsData ?? []);
      if (prepared.error === 'chamber') {
        notifications.show({
          title: 'Missing Lead Chamber',
          message: 'Please select a Chamber for all leads with data.',
          color: 'red'
        });
        return;
      }

      if (prepared.error === 'status') {
        notifications.show({
          title: 'Missing Lead Status',
          message: 'Please select a Status for all leads with data.',
          color: 'red'
        });
        return;
      }

      deviceLeadPayload = prepared.leads.map((lead) => ({
        ...lead,
        id: lead.id?.includes('tempid-') ? undefined : lead.id,
        leadChamber: lead.leadChamber?.trim() || undefined,
        deviceId: deviceInfo?.id ?? undefined,
        leadManufacturer:
          lead.leadManufacturer && lead?.leadManufacturer?.leadVendorName !== '-'
            ? {
                ...lead.leadManufacturer,
                leadVendorId:
                  typeof lead.leadManufacturer === 'object' &&
                  'leadVendorId' in lead.leadManufacturer
                    ? (lead.leadManufacturer as any).leadVendorId
                    : lead.leadManufacturer
              }
            : { leadVendorId: null },
        implantDate: lead.implantDate ? lead.implantDate : null,
        leadLocation: lead.leadLocation ? lead.leadLocation : null
      }));
    }

    setIsSaving(true);
    const payload: DeviceInfo = {
      ...formDeviceInfo,
      implantDate: (() => {
        const v = formDeviceInfo.implantDate;
        if (!v || v === '-') return null;
        const d = dayjs(v, 'MM/DD/YYYY', true).isValid()
          ? dayjs(v, 'MM/DD/YYYY', true)
          : dayjs(v);
        return d.isValid() ? d.format('YYYY-MM-DDT00:00:00') : null;
      })(),
      deviceTelemetry:
        formDeviceInfo.deviceTelemetry?.trim() === '-' ? undefined : formDeviceInfo.deviceTelemetry,
      isMriCompatibility: formDeviceInfo.isMriCompatibility
        ? formDeviceInfo.isMriCompatibility
        : deviceInfo?.isMriCompatibility,
      type: isLeadlessPM ? 'PM' : formDeviceInfo.type,
      modelNumber: isLeadlessPM
        ? `${formDeviceInfo.modelNumber}:${formDeviceInfo.modelNumber2}`
        : formDeviceInfo.modelNumber,
      serialNumber: isLeadlessPM
        ? `${formDeviceInfo.serialNumber}:${formDeviceInfo.serialNumber2}`
        : formDeviceInfo.serialNumber,
      modelNumber2: undefined,
      serialNumber2: undefined
    };

    try {
      await updateDeviceInfo({
        patientId: deviceInfo?.patientId ?? '',
        ...payload,
        reportId
      } as any).unwrap();

      // Only update leads if it's not an ILR device
      if (!isILRDevice && deviceLeadPayload && isPatientView) {
        await updateDeviceLeads({
          patientId: deviceInfo?.patientId ?? '',
          deviceLeads: deviceLeadPayload,
          deviceId: deviceInfo?.id ?? '',
          reportId: reportId ?? undefined
        }).unwrap();
      }

      let unfinishedTaskCancels = 0;
      const patientId = deviceInfo?.patientId ?? '';
      const deviceId = deviceInfo?.id ?? '';
      const becomingInactive =
        isInactiveDeviceStatus(payload.status) && !isInactiveDeviceStatus(deviceInfo?.status);
      if (becomingInactive && patientId && deviceId) {
        try {
          const openTasks = await loadOpenPatientTasks(listTasks, patientId);
          const targets = tasksToCancelOnDeviceDeactivate(openTasks, deviceId);
          for (const task of targets) {
            try {
              await cancelTask({
                id: task.id,
                body: {
                  expectedVersion: task.version,
                  reason: DEVICE_INACTIVE_TASK_CANCEL_REASON
                }
              }).unwrap();
            } catch {
              unfinishedTaskCancels += 1;
            }
          }
        } catch {
          unfinishedTaskCancels += 1;
        }
      }

      if (isInactiveDeviceStatus(payload.status)) {
        if (patientId) {
          dispatch(
            patientsApiSlice.util.invalidateTags([
              { type: API_SLICE.Patient, id: patientId },
              { type: API_SLICE.Patient, id: `${patientId}-schedules` }
            ])
          );
          dispatch(
            appointmentApiSlice.util.invalidateTags([
              { type: API_SLICE.Appointment, id: patientId }
            ])
          );
        }
        dispatch(tasksApiSlice.util.invalidateTags(['Tasks']));
      }

      notifications.show({
        title: unfinishedTaskCancels ? 'Device updated' : 'Success',
        message: unfinishedTaskCancels
          ? `Device information updated. ${unfinishedTaskCancels} open task${unfinishedTaskCancels === 1 ? '' : 's'} could not be canceled.`
          : 'Device information updated.',
        color: unfinishedTaskCancels ? 'yellow' : 'green'
      });
      setIsEditLead(false);
      onClose();
    } catch (error) {
      console.error('Device update error:', error);

      const errorMessage = getErrorMessage(error);

      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
        autoClose: false // Don't auto-close for error messages so user can read them
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditLead(false);
    onClose();
  };

  const renderSelect = (
    label: string,
    options: LabelAndValue[],
    field: keyof DeviceInfo,
    value: string | undefined,
    isDisabled = false
  ) => {
    const selected = options.find((opt) => opt.value === value);
    return (
      <Dropdown
        label={label}
        options={options}
        value={selected}
        onChange={(option) => handleFieldChange(field, (option as LabelAndValue).value)}
        placeholder={`Select ${label}`}
        labelTextProps={{ variant: 'regular' }}
        isDisabled={isDisabled}
      />
    );
  };

  const renderTextInput = (label: string, field: keyof DeviceInfo) => (
    <TextInputWrapper
      label={label}
      value={
        typeof formDeviceInfo[field] === 'boolean'
          ? String(formDeviceInfo[field])
          : (formDeviceInfo[field] ?? '')
      }
      onChange={(e) => handleFieldChange(field, e.target.value)}
    />
  );

  const getButtonText = () => {
    if (!isPatientView) return 'Save Changes';
    if (isEditLead) return 'Save Changes';
    if (isILRDevice) return 'Save Changes';
    return 'Next';
  };

  return (
    <CustomModal
      modalProps={{
        opened: isOpen,
        onClose: handleCancel,
        size: 'xl',
        closeOnClickOutside: false,
        closeOnEscape: false
      }}
      header={{ title: 'Edit Device Info' }}
      primaryButton={{
        text: getButtonText(),
        isLoading: isSaving,
        onClick: handleSave
      }}
      secondaryButton={{ text: 'Cancel', isDisabled: isSaving, onClick: handleCancel }}>
      {isEditLead ? (
        <LeadsEditModal
          leadsData={leadsData}
          onLeadFieldChange={handleLeadFieldChange}
          onAddLead={handleAddLead}
          onDeleteLead={handleDeleteLead}
          leadStatusErrors={leadStatusErrors}
          onLeadStatusError={handleLeadStatusError}
          deviceId={deviceInfo?.id}
        />
      ) : (
        <div className={styles.deviceSection}>
          <Grid mt="md" align="center">
            <Grid.Col span={{ base: 12, sm: 4 }}>
              {renderSelect('Manufacturer', MANUFACTURER_OPTIONS, 'vendor', formDeviceInfo.vendor)}
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 4 }}>
              {renderSelect(
                'Type',
                getDeviceTypeOptions(formDeviceInfo.vendor),
                'type',
                formDeviceInfo.type
              )}
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 4 }}>
              {renderTextInput('Model Name', 'modelName')}
            </Grid.Col>

            {isLeadlessPM ? (
              <>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  {renderTextInput('Model Number 1', 'modelNumber')}
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 4 }}>
                  {renderTextInput('Model Number 2', 'modelNumber2')}
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 4 }}>
                  {renderTextInput('Serial Number 1', 'serialNumber')}
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 4 }}>
                  {renderTextInput('Serial Number 2', 'serialNumber2')}
                </Grid.Col>
              </>
            ) : (
              <>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  {renderTextInput('Model Number', 'modelNumber')}
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 4 }}>
                  {renderTextInput('Serial Number', 'serialNumber')}
                </Grid.Col>
              </>
            )}

            <Grid.Col span={{ base: 12, sm: 4 }}>
              <DatePickerWrapper
                inputProps={{ label: 'Implant Date' }}
                datePickerProps={{
                  id: 'implantDate',
                  selected: (() => {
                    const v = formDeviceInfo.implantDate;
                    if (!v || v === '-') return null;
                    // Try MM/DD/YYYY first (what the selector formats it as), then ISO
                    const d = dayjs(v, 'MM/DD/YYYY', true).isValid()
                      ? dayjs(v, 'MM/DD/YYYY', true).toDate()
                      : dayjs(v).isValid()
                        ? dayjs(v).toDate()
                        : null;
                    return d;
                  })(),
                  onChange: (date: Date | null) => handleImplantDateChange(date),
                  isClearable: true
                }}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 4 }}>
              {renderSelect(
                'Device Status',
                STATUS_OPTIONS,
                'status',
                String(formDeviceInfo.status)
              )}
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 4 }}>
              {renderSelect(
                'Device Telemetry',
                DEVICE_TELEMETRY_OPTIONS,
                'deviceTelemetry',
                String(formDeviceInfo.deviceTelemetry),
                isPatientView
              )}
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 4 }}>
              {renderSelect(
                'MRI Compatibility',
                MRI_COMPATIBILITY_OPTIONS,
                'isMriCompatibility',
                String(formDeviceInfo.isMriCompatibility)
              )}
            </Grid.Col>
          </Grid>
        </div>
      )}
    </CustomModal>
  );
};

export default DeviceEditModal;
